import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { apiDelete, apiPost, apiPut } from '@/api/client';
import type { CompanyLocation } from '@/api/types';
import { SubmitError } from '@/app/components/SubmitError';
import { Button } from '@/app/components/ui/Button';
import { Field, Input, Select } from '@/app/components/ui/Field';
import { unlinkDepartmentFromPilot } from '@/features/pilots/api';
import { useSubmit } from '@/lib/useSubmit';

/**
 * Row-level member actions shared by the company member table. Each is small,
 * fires one endpoint, and reports its 403/409 inline in the row.
 */

/** POST /users/{id}/invite — give a login-less identity a login, or resend. */
export function InviteButton({ userId, onDone }: { userId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const form = useSubmit(async () => {
    const result = (await apiPost('/users/{userId}/invite', { params: { userId } })) as
      { invite_sent?: boolean } | undefined;
    setSent(result?.invite_sent ?? false);
    onDone();
  });

  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        variant="secondary"
        size="sm"
        disabled={form.pending}
        onClick={() => void form.submit()}
      >
        {sent ? t('users.actions.inviteAgain') : t('users.actions.invite')}
      </Button>
      <SubmitError error={form.error} />
    </div>
  );
}

/**
 * PUT /users/{id}/email — admin or the company's own admin; the new address
 * lands unverified and Keycloak asks the person to confirm it. Never shown to
 * a department admin: the address is the login identity.
 */
export function ChangeEmailForm({
  userId,
  currentEmail,
  onDone,
}: {
  userId: string;
  currentEmail: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(currentEmail);
  const form = useSubmit(async () => {
    await apiPut('/users/{userId}/email', { params: { userId }, body: { email } });
    setOpen(false);
    onDone();
  });

  if (!open) {
    return (
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        {t('users.actions.changeEmail')}
      </Button>
    );
  }

  return (
    <form
      className="flex flex-col items-start gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        void form.submit();
      }}
    >
      <Field label={t('users.actions.newEmail')} className="min-w-64">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </Field>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={form.pending}>
          {t('users.actions.save')}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
          {t('users.actions.cancel')}
        </Button>
      </div>
      <SubmitError error={form.error} />
    </form>
  );
}

/** DELETE /companies/{id}/users — admin only: end a membership, keep the person. */
export function RemoveMembershipButton({
  companyId,
  userId,
  onDone,
}: {
  companyId: string;
  userId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const form = useSubmit(async () => {
    await apiDelete('/companies/{companyId}/users', {
      params: { companyId },
      body: { user_id: userId },
    });
    onDone();
  });

  return (
    <div className="flex flex-col items-end gap-1">
      <Button variant="danger" size="sm" disabled={form.pending} onClick={() => void form.submit()}>
        {t('users.actions.remove')}
      </Button>
      <SubmitError error={form.error} />
    </div>
  );
}

/** DELETE /departments/{id}/users — company admin: remove a member from a department. */
export function RemoveDepartmentMemberButton({
  departmentId,
  userId,
  onDone,
}: {
  departmentId: string;
  userId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const form = useSubmit(async () => {
    await apiDelete('/departments/{departmentId}/users', {
      params: { departmentId },
      body: { user_id: userId },
    });
    onDone();
  });

  return (
    <div className="flex flex-col items-end gap-1">
      <Button variant="danger" size="sm" disabled={form.pending} onClick={() => void form.submit()}>
        {t('users.actions.remove')}
      </Button>
      <SubmitError error={form.error} />
    </div>
  );
}

/** DELETE /users/{id}/link — the alias becomes standalone again. */
export function UnlinkButton({ userId, onDone }: { userId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const form = useSubmit(async () => {
    await apiDelete('/users/{userId}/link', { params: { userId } });
    onDone();
  });

  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        variant="secondary"
        size="sm"
        disabled={form.pending}
        onClick={() => void form.submit()}
      >
        {t('users.actions.unlink')}
      </Button>
      <SubmitError error={form.error} />
    </div>
  );
}

/**
 * PUT /users/{id}/location — a plain select, changed on the Medewerkers
 * list's row (PLAN-FASE2.md WP-01 "locatie wijzigen"). No confirm step:
 * unlike removing a membership this is easily reversed.
 */
export function ChangeLocationSelect({
  userId,
  locationId,
  locations,
  onDone,
}: {
  userId: string;
  locationId: string | null;
  locations: CompanyLocation[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const form = useSubmit(async (nextLocationId: string) => {
    await apiPut('/users/{userId}/location', {
      params: { userId },
      body: { location_id: nextLocationId === '' ? null : nextLocationId },
    });
    onDone();
  });

  return (
    <div className="flex flex-col items-start gap-1">
      <Select
        value={locationId ?? ''}
        disabled={form.pending}
        onChange={(e) => void form.submit(e.target.value)}
        aria-label={t('users.actions.changeLocation')}
        className="min-w-32"
      >
        <option value="">{t('users.page.noLocation')}</option>
        {locations.map((location) => (
          <option key={location.id} value={location.id}>
            {location.name}
          </option>
        ))}
      </Select>
      <SubmitError error={form.error} />
    </div>
  );
}

/**
 * Moves someone from their current department into another one
 * (PLAN-FASE2.md WP-01 "afdeling wijzigen"): `DELETE .../users` on the old
 * department, `POST .../users` on the new one — the two existing membership
 * endpoints, no new API surface. Leaving the department entirely is
 * `RemoveDepartmentMemberButton` below, not this control.
 */
export function ChangeDepartmentSelect({
  userId,
  departmentId,
  departments,
  onDone,
}: {
  userId: string;
  departmentId: string | null;
  departments: { id: string; name: string }[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const form = useSubmit(async (nextDepartmentId: string) => {
    if (departmentId !== null) {
      await apiDelete('/departments/{departmentId}/users', {
        params: { departmentId },
        body: { user_id: userId },
      });
    }
    if (nextDepartmentId !== '') {
      await apiPost('/departments/{departmentId}/users', {
        params: { departmentId: nextDepartmentId },
        body: { user_id: userId },
      });
    }
    onDone();
  });

  return (
    <div className="flex flex-col items-start gap-1">
      <Select
        value={departmentId ?? ''}
        disabled={form.pending}
        onChange={(e) => void form.submit(e.target.value)}
        aria-label={t('users.actions.changeDepartment')}
        className="min-w-32"
      >
        <option value="">{t('users.page.noDepartment')}</option>
        {departments.map((department) => (
          <option key={department.id} value={department.id}>
            {department.name}
          </option>
        ))}
      </Select>
      <SubmitError error={form.error} />
    </div>
  );
}

/**
 * PUT /users/{id}/deactivate and .../activate — the Medewerkers list's
 * "deactiveren" row action (PLAN-FASE2.md WP-01), reversible unlike erasure.
 */
export function DeactivateToggle({
  userId,
  deactivated,
  onDone,
}: {
  userId: string;
  deactivated: boolean;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const form = useSubmit(async () => {
    await apiPut(deactivated ? '/users/{userId}/activate' : '/users/{userId}/deactivate', {
      params: { userId },
    });
    onDone();
  });

  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        variant={deactivated ? 'secondary' : 'danger'}
        size="sm"
        disabled={form.pending}
        onClick={() => void form.submit()}
      >
        {deactivated ? t('users.actions.activate') : t('users.actions.deactivate')}
      </Button>
      <SubmitError error={form.error} />
    </div>
  );
}

/** The erasure entry point is a page of its own — never a one-click row action. */
export function EraseLink({ userId }: { userId: string }) {
  const { t } = useTranslation();
  return (
    <Button as={Link} variant="danger" size="sm" to={`/manage/users/${userId}/erase`}>
      {t('users.actions.erase')}
    </Button>
  );
}

/**
 * "Ontkoppelen van pilot" (WP-04): a pilot's pilotgroep is department-based,
 * not person-based (`api_pilot_departments`, no per-user row), so this row
 * action removes the person's *department* from the pilot — every colleague
 * in that department loses the pilot link too. `Pilot: Ja` in the Users
 * table comes from the same relationship, so this is the row action that
 * flips it back to "–", exactly as PLAN-FASE2.md WP-01 describes it.
 */
export function UnlinkFromPilotButton({
  pilotId,
  departmentId,
  onDone,
}: {
  pilotId: string;
  departmentId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const form = useSubmit(async () => {
    await unlinkDepartmentFromPilot(pilotId, departmentId);
    onDone();
  });

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        variant="secondary"
        size="sm"
        disabled={form.pending}
        onClick={() => void form.submit()}
      >
        {t('users.actions.unlinkFromPilot')}
      </Button>
      <SubmitError error={form.error} />
    </div>
  );
}
